import { useQuery, useMutation } from '@tanstack/react-query';
import { Check, Loader2 } from 'lucide-react';
import { api } from '@/lib/apiClient';
import { Button } from '@/components/ui/button';
import type { BillingPlan } from '@/types/api';
import { StepFooter } from './StepFooter';

// The plan shown as the recommended choice.
const HIGHLIGHTED_PLAN = 'pro';

interface PlanStepProps {
  /** Record the wizard as finished. Resolves once the server has it, successful or not. */
  finish: () => Promise<unknown>;
  /** Finish and go to the dashboard ("Decide later"). */
  onDone: () => void;
  finishing: boolean;
}

export function PlanStep({ finish, onDone, finishing }: PlanStepProps) {
  const plans = useQuery({
    queryKey: ['billing', 'plans'],
    queryFn: () => api.get<BillingPlan[]>('/api/billing/plans'),
    staleTime: 10 * 60 * 1000,
  });

  const subscribe = useMutation({
    mutationFn: (planId: string) => api.post<{ redirectUrl: string }>('/api/billing/subscribe', { planId }),
    // Checkout returns to Settings, not here, so record the wizard as finished before leaving.
    // A failure (e.g. billing not configured yet) is already shown by the API client's toast.
    onSuccess: async (data) => {
      if (!data?.redirectUrl) return;
      await finish();
      window.location.href = data.redirectUrl;
    },
  });

  const busy = subscribe.isPending || finishing;
  // Annotated: `plans.data ?? []` is `BillingPlan[] | never[]`, and TS 4.9 cannot type the
  // callback parameters of .map on a union of array types.
  const planList: BillingPlan[] = plans.data ?? [];

  return (
    <div>
      <h2 className="text-xl font-semibold text-slate-900 dark:text-white">Choose a plan</h2>
      <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">Pick one now or later from Settings. You can change plans at any time.</p>

      {plans.isLoading && (
        <div className="mt-6 flex justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-primary" aria-label="Loading plans" />
        </div>
      )}

      {plans.isError && (
        <div className="mt-6 rounded-md border border-amber-200 dark:border-amber-800/60 bg-amber-50 dark:bg-amber-950/40 px-4 py-3 text-sm text-amber-900 dark:text-amber-300">
          Plans could not be loaded right now. You can choose one later from Settings.{' '}
          <button type="button" className="font-medium underline" onClick={() => plans.refetch()}>
            Try again
          </button>
        </div>
      )}
      {plans.isSuccess && planList.length === 0 && (
        <p className="mt-6 rounded-md bg-slate-50 dark:bg-slate-900/50 px-4 py-3 text-sm text-slate-600 dark:text-slate-400">
          No plans are available yet. You can continue without choosing one.
        </p>
      )}

      <div className="mt-6 grid gap-3 sm:grid-cols-3">
        {planList.map((plan) => {
          const highlighted = plan.key === HIGHLIGHTED_PLAN;
          const choosing = subscribe.isPending && subscribe.variables === plan.id;
          return (
            <div
              key={plan.id}
              className={`flex flex-col rounded-lg border p-4 transition-colors ${
                highlighted ? 'border-primary ring-1 ring-primary bg-primary/[0.02] dark:bg-primary/[0.05]' : 'border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/40'
              }`}
            >
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-slate-900 dark:text-white">{plan.name}</h3>
                {highlighted && (
                  <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary">
                    Popular
                  </span>
                )}
              </div>
              <p className="mt-2 text-2xl font-bold text-slate-900 dark:text-white">
                ৳{plan.priceBdt.toLocaleString('en-US')}
                <span className="text-sm font-normal text-slate-500 dark:text-slate-400"> / {plan.interval}</span>
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400">or ${(plan.priceUsd / 100).toFixed(0)} / {plan.interval}</p>
              <ul className="mt-3 flex-1 space-y-1.5">
                {plan.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-1.5 text-xs text-slate-600 dark:text-slate-300">
                    <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" aria-hidden="true" />
                    {feature}
                  </li>
                ))}
              </ul>
              <Button
                className="mt-4 w-full"
                variant={highlighted ? 'default' : 'outline'}
                size="sm"
                disabled={busy}
                onClick={() => subscribe.mutate(plan.id)}
              >
                {choosing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Choose {plan.name}
              </Button>
            </div>
          );
        })}
      </div>

      <StepFooter onSkip={onDone} skipLabel="Decide later" primaryLabel="Finish" onPrimary={onDone} busy={busy} />
    </div>
  );
}
